'use client';

import { motion, useAnimationControls } from 'framer-motion';
import { useLayoutEffect, useRef, useState } from 'react';

type AnimateHeightProps = {
  children: React.ReactNode;
  className?: string;
  /** Animation duration in seconds. */
  duration?: number;
};

/**
 * Wraps content and smoothly animates its height whenever the content's
 * natural height changes (e.g. swapping between steps or toggling content).
 *
 * The inner wrapper is measured with a ResizeObserver, so it reacts to any
 * size change without needing to know anything about the children.
 */
export const AnimateHeight = ({ children, className, duration = 0.3 }: AnimateHeightProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>('auto');
  // Skip animating on the very first measurement so content appears at its natural size.
  const hasMeasured = useRef(false);
  // Tracks the last measured height so we can tell whether the content is growing or shrinking.
  const prevHeight = useRef<number | null>(null);
  const contentControls = useAnimationControls();

  useLayoutEffect(() => {
    const el = contentRef.current;

    if (!el) return;

    const observer = new ResizeObserver(() => {
      const next = el.offsetHeight;

      /*
       * When collapsing to a shorter height, briefly dip the content's opacity
       * and bring it back so the disappearing region fades out instead of snapping away.
       */
      if (prevHeight.current != null && next < prevHeight.current) {
        contentControls.start({ opacity: [1, 0.4, 1] }, { duration, ease: 'easeInOut' });
      }

      prevHeight.current = next;
      setHeight(next);
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [contentControls, duration]);

  return (
    <motion.div
      animate={{ height }}
      transition={hasMeasured.current ? { duration, ease: 'easeInOut' } : { duration: 0 }}
      onAnimationComplete={() => { hasMeasured.current = true; }}
      className={className}
    >
      <motion.div ref={contentRef} animate={contentControls}>
        {children}
      </motion.div>
    </motion.div>
  );
};

export default AnimateHeight;
